import { useEffect, useMemo, useRef, useState } from 'react'

import { gsap, Power1 } from 'gsap'
import { useRouter } from 'next/router'
import { useIntl } from 'react-intl'

import { assetImagePath } from 'interfaces/constants'

import { ProgressBarAnimation } from '../progressBar'
import {
  StyledContainer,
  StyledDataContainer,
  StyledDataWrapper,
  StyledDotsContainer,
  StyledDot,
  StyledPreviousMap,
  StyledNextMap,
} from './index.styled'

type RendershipProps = {
  country: string
  locale: string
  bannerImgDesktop: string
  bannerImgMobile: string
  datasets: {
    label: string
    value: number
  }[]
}

type Props = {
  isMobile: boolean
  topbarHeight?: number
}

const BlockingAnimationTime = 1000

const HealthRendership = ({ isMobile, topbarHeight = 64 }: Props) => {
  const { locale = 'en-PH' } = useRouter()

  const { formatMessage: f } = useIntl()
  const [currentIndex, setCurrentIndex] = useState(0)

  const [bannerHeight, setBannerHeight] = useState(isMobile ? 603 : 831)

  const enableSlide = useRef(true)

  const enableWheel = useRef(true)

  const [contentPadding, setContentPadding] = useState(1051)

  const healthRendershipData: RendershipProps[] = useMemo(
    () => [
      {
        country: f({
          id: 'superContent.chart.healthTool.items.indonesia',
          defaultMessage: 'Indonesia',
        }),
        locale: 'id-ID',
        bannerImgDesktop: `${assetImagePath}/health-rendership-indonesia-desktop.png?v=1`,
        bannerImgMobile: `${assetImagePath}/health-rendership-indonesia.png?v=1`,
        datasets: [
          {
            label: f({
              id: 'superContent.healthRendership.stressManagement',
              defaultMessage: 'Stress management',
            }),
            value: 44,
          },
          {
            label: f({
              id: 'superContent.healthRendership.weightManagement',
              defaultMessage: 'Weight management',
            }),
            value: 40,
          },
          {
            label: f({
              id: 'superContent.healthRendership.depression',
              defaultMessage: 'Depression',
            }),
            value: 22,
          },
        ],
      },
      {
        country: f({
          id: 'superContent.chart.healthTool.items.malaysia',
          defaultMessage: 'Malaysia',
        }),
        locale: 'ms-MY',
        bannerImgDesktop: `${assetImagePath}/health-rendership-malaysia-desktop.png?v=1`,
        bannerImgMobile: `${assetImagePath}/health-rendership-malaysia.png?v=1`,
        datasets: [
          {
            label: f({
              id: 'superContent.healthRendership.stressManagement',
              defaultMessage: 'Stress management',
            }),
            value: 43,
          },
          {
            label: f({
              id: 'superContent.healthRendership.weightManagement',
              defaultMessage: 'Weight management',
            }),
            value: 39,
          },
          {
            label: f({
              id: 'superContent.healthRendership.depression',
              defaultMessage: 'Depression',
            }),
            value: 29,
          },
        ],
      },
      {
        country: f({
          id: 'superContent.chart.healthTool.items.taiwan',
          defaultMessage: 'Taiwan',
        }),
        locale: 'zh-TW',
        bannerImgDesktop: `${assetImagePath}/health-rendership-taiwan-desktop.png?v=1`,
        bannerImgMobile: `${assetImagePath}/health-rendership-taiwan.png?v=1`,
        datasets: [
          {
            label: f({
              id: 'superContent.healthRendership.weightManagement',
              defaultMessage: 'Weight management',
            }),
            value: 40,
          },
          {
            label: f({
              id: 'superContent.healthRendership.cancer',
              defaultMessage: 'Cancer',
            }),
            value: 37,
          },
          {
            label: f({
              id: 'superContent.healthRendership.stressManagement',
              defaultMessage: 'Stress management',
            }),
            value: 31,
          },
        ],
      },
      {
        country: f({
          id: 'superContent.chart.healthTool.items.myanmar',
          defaultMessage: 'Myanmar',
        }),
        locale: 'my-MM',
        bannerImgDesktop: `${assetImagePath}/health-rendership-myanmar-desktop.png?v=1`,
        bannerImgMobile: `${assetImagePath}/health-rendership-myanmar.png?v=1`,
        datasets: [
          {
            label: f({
              id: 'superContent.healthRendership.infectious',
              defaultMessage: 'Infectious diseases',
            }),
            value: 36,
          },
          {
            label: f({
              id: 'superContent.healthRendership.stressManagement',
              defaultMessage: 'Stress management',
            }),
            value: 35,
          },
          {
            label: f({
              id: 'superContent.healthRendership.weightManagement',
              defaultMessage: 'Weight management',
            }),
            value: 28,
          },
        ],
      },
      {
        country: f({
          id: 'superContent.chart.healthTool.items.thailand',
          defaultMessage: 'Thailand',
        }),
        locale: 'th-TH',
        bannerImgDesktop: `${assetImagePath}/health-rendership-thailand-desktop.png?v=1`,
        bannerImgMobile: `${assetImagePath}/health-rendership-thailand.png?v=1`,
        datasets: [
          {
            label: f({
              id: 'superContent.healthRendership.cancer',
              defaultMessage: 'Cancer',
            }),
            value: 35,
          },
          {
            label: f({
              id: 'superContent.healthRendership.weightManagement',
              defaultMessage: 'Weight management',
            }),
            value: 32,
          },
          {
            label: f({
              id: 'superContent.healthRendership.stressManagement',
              defaultMessage: 'Stress management',
            }),
            value: 29,
          },
        ],
      },
      {
        country: f({
          id: 'superContent.chart.healthTool.items.india',
          defaultMessage: 'India',
        }),
        locale: 'hi-IN',
        bannerImgDesktop: `${assetImagePath}/health-rendership-india-desktop.png?v=1`,
        bannerImgMobile: `${assetImagePath}/health-rendership-india.png?v=1`,
        datasets: [
          {
            label: f({
              id: 'superContent.healthRendership.weightManagement',
              defaultMessage: 'Weight management',
            }),
            value: 45,
          },
          {
            label: f({
              id: 'superContent.healthRendership.joinPain',
              defaultMessage: 'Joint pain (strains, pains, injuries)',
            }),
            value: 31,
          },
          {
            label: f({
              id: 'superContent.healthRendership.sexualWellness',
              defaultMessage: 'Sexual wellness',
            }),
            value: 31,
          },
          {
            label: f({
              id: 'superContent.healthRendership.stressManagement',
              defaultMessage: 'Stress management',
            }),
            value: 26,
          },
        ],
      },
    ],
    [],
  )
  const swipeBegin = useRef({
    x: -1,
    y: -1,
  })
  const tl = useRef({})
  const indx = useRef({
    index: 0,
  })
  const el = useRef<HTMLDivElement>(null)
  const q = gsap.utils.selector(el)

  const data = useMemo(
    () => [
      ...healthRendershipData.filter((el) => el.locale === locale),
      ...healthRendershipData.filter((el) => el.locale !== locale),
    ],
    [],
  )

  const slideDown = () => {
    if (enableSlide.current) {
      enableSlide.current = false
      setTimeout(() => {
        enableSlide.current = true
      }, BlockingAnimationTime)
      tl.current = gsap
        .timeline()
        .to(q('.animation-image-post'), {
          opacity: 0,
          duration: 1,
        })
        .to(
          q('.animation-image-main'),
          {
            opacity: 1,
            duration: 1,
          },
          0,
        )
        .fromTo(
          q('.country-title'),
          {
            opacity: 0,
            x: 50,
          },
          {
            opacity: 1,
            x: 0,
            duration: 1,
          },
          0,
        )
        .fromTo(
          q('.data-value'),
          {
            opacity: 0,
            x: 30,
          },
          {
            opacity: 1,
            x: 0,
            duration: 1,
          },
          0,
        )
        .from(
          q('.value-number'),
          {
            textContent: 0,
            snap: { textContent: 1 },
            duration: 1,
          },
          0,
        )
    }
  }

  const slideUp = () => {
    if (enableSlide.current) {
      enableSlide.current = false
      setTimeout(() => {
        enableSlide.current = true
      }, BlockingAnimationTime)
      tl.current = gsap
        .timeline()
        .to(q('.animation-image-main'), {
          opacity: 1,
          duration: 1,
        })
        .to(
          '.animation-image-after',
          {
            opacity: 0,
            duration: 1,
          },
          0,
        )
        .fromTo(
          q('.country-title'),
          {
            opacity: 0,
            x: 50,
          },
          {
            opacity: 1,
            x: 0,
            duration: 1,
          },
          0,
        )
        .fromTo(
          q('.data-value'),
          {
            opacity: 0,
            x: 30,
          },
          {
            opacity: 1,
            x: 0,
            duration: 1,
          },
          0,
        )
        .from(
          q('.value-number'),
          {
            textContent: 0,
            snap: { textContent: 1 },
            duration: 1,
            ease: Power1.easeIn,
          },
          0,
        )
    }
  }
  useEffect(() => {
    if (indx.current.index <= currentIndex) {
      slideDown()
    } else {
      slideUp()
    }
    indx.current.index = currentIndex
  }, [currentIndex])

  useEffect(() => {
    if (typeof window !== 'undefined' && window?.innerHeight > 0) {
      const bannerHeight = isMobile
        ? window?.innerHeight
        : window?.innerHeight - topbarHeight
      const bannerWidth = (bannerHeight * 1440) / 955
      setBannerHeight(bannerHeight)
      setContentPadding(
        bannerWidth * 0.73 + (window.innerWidth - bannerWidth) / 2,
      )
    }
  }, [])

  const currentCountry = data?.[currentIndex]

  const handleWheel = (type: 'next' | 'previous') => {
    if (enableWheel.current && enableSlide.current) {
      enableWheel.current = false

      setTimeout(() => {
        enableWheel.current = true
      }, BlockingAnimationTime)

      if (type === 'next') {
        typeof window !== 'undefined' &&
          currentIndex < data.length - 1 &&
          window.scrollTo({
            top: isMobile
              ? el?.current?.offsetTop
              : (el?.current?.offsetTop || 64) - 64,
            behavior: 'smooth',
          })

        setCurrentIndex((v) => (v > data.length - 2 ? data.length - 1 : v + 1))
      } else {
        typeof window !== 'undefined' &&
          currentIndex > 0 &&
          window.scrollTo({
            top: isMobile
              ? el?.current?.offsetTop
              : (el?.current?.offsetTop || 64) - 64,
            behavior: 'smooth',
          })

        setCurrentIndex((v) => (v < 1 ? 0 : v - 1))
      }
    }
  }

  const handleChangeCountry = (newIndex: number) => {
    if (enableWheel.current && enableSlide.current) {
      enableWheel.current = false

      setTimeout(() => {
        enableWheel.current = true
      }, BlockingAnimationTime)

      typeof window !== 'undefined' &&
        window.scrollTo({
          top: isMobile
            ? el?.current?.offsetTop
            : (el?.current?.offsetTop || 64) - 64,
          behavior: 'smooth',
        })

      setCurrentIndex(newIndex)
    }
  }

  const handleSlide = (type: 'left' | 'right') => {
    if (enableWheel.current && enableSlide.current) {
      enableWheel.current = false

      if (type === 'left') {
        typeof window !== 'undefined' &&
          currentIndex > 0 &&
          window.scrollTo({
            top: isMobile
              ? el?.current?.offsetTop
              : (el?.current?.offsetTop || 64) - 64,
            behavior: 'smooth',
          })

        setCurrentIndex((v) => (v < 1 ? 0 : v - 1))
      } else {
        typeof window !== 'undefined' &&
          currentIndex < data.length - 1 &&
          window.scrollTo({
            top: isMobile
              ? el?.current?.offsetTop
              : (el?.current?.offsetTop || 64) - 64,
            behavior: 'smooth',
          })
        setCurrentIndex((v) => (v > data.length - 2 ? data.length - 1 : v + 1))
      }

      setTimeout(() => {
        enableWheel.current = true
      }, BlockingAnimationTime)
    }
  }

  return (
    <StyledContainer
      data-banner-height={bannerHeight}
      ref={el}
      onTouchStart={(e) => {
        swipeBegin.current.x = e.changedTouches[0].clientX
        swipeBegin.current.y = e.changedTouches[0].clientY
      }}
      onTouchEnd={(e) => {
        if (
          swipeBegin.current.x > 0 &&
          Math.abs(e.changedTouches[0].clientX - swipeBegin.current.x) >
            Math.abs(e.changedTouches[0].clientY - swipeBegin.current.y)
        ) {
          handleSlide(
            e.changedTouches[0].clientX - swipeBegin.current.x > 0
              ? 'left'
              : 'right',
          )
        }
        swipeBegin.current.x = -1
        swipeBegin.current.y = -1
      }}
    >
      {data?.map((data, index) => (
        <img
          key={index}
          loading="lazy"
          className={`animation animation-image${
            index < currentIndex ? ' animation-image-post' : ''
          }${currentIndex === index ? ' animation-image-main' : ''}${
            currentIndex < index ? ' animation-image-after' : ''
          }`}
          src={isMobile ? data?.bannerImgMobile : data?.bannerImgDesktop}
          alt={f({
            id: 'superContent.healthRendership.img.alt',
            defaultMessage: 'Southeast Asians’ Top Health Concerns',
          })}
        />
      ))}
      <StyledDataContainer data-content-padding={contentPadding}>
        <div className="country-title">{currentCountry?.country}</div>
        {currentCountry?.datasets?.map((category, index) => (
          <StyledDataWrapper key={index}>
            <div className="data-label">{category?.label}</div>
            <div className="progress-bar">
              <ProgressBarAnimation
                country={currentCountry?.country}
                value={category?.value}
              />
              <div className="data-value">
                <div className="value-number">{category?.value}</div>
                {'%'}
              </div>
            </div>
          </StyledDataWrapper>
        ))}
      </StyledDataContainer>
      {!isMobile ? (
        <>
          {currentIndex !== 0 ? (
            <StyledPreviousMap>
              <img
                src={`${assetImagePath}/chevronLeftCircle.svg`}
                alt="previous"
                onClick={() => handleWheel('previous')}
                loading="lazy"
              />
            </StyledPreviousMap>
          ) : null}
          {currentIndex < data.length - 1 ? (
            <StyledNextMap>
              <img
                src={`${assetImagePath}/chevronRightCircle.svg`}
                alt="next"
                onClick={() => handleWheel('next')}
                loading="lazy"
              />
            </StyledNextMap>
          ) : null}
        </>
      ) : null}
      <StyledDotsContainer>
        {data?.map((e, index) => (
          <StyledDot
            key={`${e?.country}-${index}`}
            data-active={index === currentIndex}
            onClick={() => handleChangeCountry(index)}
          ></StyledDot>
        ))}
      </StyledDotsContainer>
    </StyledContainer>
  )
}

export { HealthRendership }
